import React from 'react';
import { Box, Paper, Typography, Alert, CircularProgress } from '@mui/material';
import { useUserAccess } from '../../hooks/useUserAccess';
import { UserAccessHeader } from './UserAccessHeader';
import { SearchBar } from './SearchBar';
import { UserListTable } from './UserListTable';
import { AddUserModal } from './AddUserModal';
import { DeleteUserModal } from './DeleteUserModal';
import type { UserAccessPageProps } from './types';
import { DART_LOGO } from '@/config/constant';
import './UserAccess.css';

export const UserAccessPage: React.FC<UserAccessPageProps> = () => {
  const {
    // Data state
    filteredUsers,
    paginatedUsers,
    
    // UI state
    loading,
    error,
    success,
    
    // Modal state
    showAddModal,
    showDeleteModal,
    userToDelete,
    
    // Search & Pagination state
    searchQuery,
    page,
    rowsPerPage,
    
    // Sorting state
    sortField,
    sortOrder,
    
    // Form state
    newUser,
    
    // Handlers
    handleAddUser,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleSearchReset,
    handleExportUsers,
    handleBackToDashboard,
    handleRoleChange,
    handleEmailChange,
    handleSearchChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleAddModalOpen,
    handleAddModalClose,
    handleSort,
    clearError,
    clearSuccess,
  } = useUserAccess();

  return (
    <Box className="user-access-page" sx={{ bgcolor: 'background.default' }}>
      {/* Logo / title bar */}
      <Box className="user-access-page__header" sx={{ bgcolor: 'background.paper' }}>
        <div className="user-access-page__logo-bar">
          <img
            src={DART_LOGO}
            alt="DART Logo"
            className="user-access-page__logo"
          />
          <Typography variant="h6" component="h1" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
            |
          </Typography>
          <Typography variant="h6" component="h1" sx={{ fontWeight: 600, fontSize: '1.5rem' }}>
            Manage User Access
          </Typography>
        </div>
      </Box>

      {/* Main content */}
      <div className="user-access-page__content">
        <Paper elevation={1} className="user-access-paper">
          {error && (
            <Alert severity="error" onClose={clearError} className="user-access-alert">
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={clearSuccess} className="user-access-alert">
              {success}
            </Alert>
          )}

          <UserAccessHeader
            loading={loading}
            filteredUsersCount={filteredUsers.length}
            onAddUser={handleAddModalOpen}
            onExportUsers={handleExportUsers}
            onBackToDashboard={handleBackToDashboard}
          />

          <SearchBar
            searchQuery={searchQuery}
            loading={loading}
            onSearchChange={handleSearchChange}
            onReset={handleSearchReset}
          />

          {loading ? (
            <div className="user-access-loader">
              <CircularProgress />
            </div>
          ) : (
            <UserListTable
              users={paginatedUsers}
              totalCount={filteredUsers.length}
              loading={loading}
              searchQuery={searchQuery}
              page={page}
              rowsPerPage={rowsPerPage}
              sortField={sortField}
              sortOrder={sortOrder}
              onDeleteClick={handleDeleteClick}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              onSort={handleSort}
            />
          )}
        </Paper>

        <AddUserModal
          open={showAddModal}
          loading={loading}
          newUser={newUser}
          onClose={handleAddModalClose}
          onEmailChange={handleEmailChange}
          onRoleChange={handleRoleChange}
          onSubmit={handleAddUser}
        />

        <DeleteUserModal
          open={showDeleteModal}
          loading={loading}
          userEmail={userToDelete}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      </div>
    </Box>
  );
};

export default UserAccessPage;